// Plan & billing: account status, trial controls, package, notes, Stripe
// ids. Every control saves on its own through
// PATCH /platform/leagues/:id/account, which returns the tenant (so the
// Live badge is always the server's, #332). The checkout link was removed
// at the 1.0 launch: leagues choose a plan themselves on Plan & billing.
//
// Anything that would take a live public site offline (cancel, end the
// trial) asks first. Suspend lives in the Danger zone.

import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import type { AccountPatch, AccountStatus, TenantDetail } from '@/api/types'
import {
  AccountStatusPill,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EditableSelectField,
  EditableTextField,
  FormField,
  InlineAlert,
  KeyValueList,
  LivePill,
  SaveStatus,
  TextInput,
  useSaveAction,
} from '@/components/ui'
import { cn, daysUntil, formatDate, formatDateTime, toDateInput } from '@/lib/format'
import { tenantLinks } from '@/lib/tenant'
import { useTenant } from '../tenantContext'

const DAY = 86_400_000
const TRIAL_CAP_DAYS = 180
const DEFAULT_TRIAL_DAYS = 14
const BILLING_LABEL = { stripe: 'Stripe', manual: 'Manual (no Stripe)', none: 'None' } as const

/** The contract's liveness rule (API_CONTRACT.md › is_live). */
function liveAfter(status: AccountStatus, trialEndsAt: string | null, now = Date.now()): boolean {
  if (status === 'active' || status === 'past_due') return true
  if (status === 'trialing') return !!trialEndsAt && new Date(trialEndsAt).getTime() > now
  return false
}

interface PendingChange {
  patch: AccountPatch
  /** Button label, e.g. "Mark canceled". */
  label: string
}

export default function PlanTab() {
  const { tenant, apply } = useTenant()
  const a = tenant.account
  const [warning, setWarning] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<PendingChange | null>(null)
  const action = useSaveAction()
  const host = tenantLinks(tenant).publicHost

  /** Runs one account PATCH and adopts the tenant it returns. Throws on failure. */
  const patchAccount = async (patch: AccountPatch) => {
    const r = await api.tenants.updateAccount(tenant.id, patch)
    apply(r.tenant)
    setWarning(r.warning)
  }

  /** Status/trial actions: confirm first if the site would go offline. */
  const request = (change: PendingChange) => {
    const nextStatus = (change.patch.status ?? a.status) as AccountStatus
    const nextTrial = 'trial_ends_at' in change.patch ? change.patch.trial_ends_at ?? null : a.trial_ends_at
    if (a.is_live && !liveAfter(nextStatus, nextTrial)) {
      setConfirm(change)
      return
    }
    action.run(() => patchAccount(change.patch))
  }

  const suspended = a.status === 'suspended'
  const now = Date.now()
  const trialLeft = daysUntil(a.trial_ends_at)
  const trialBase = a.trial_ends_at && new Date(a.trial_ends_at).getTime() > now ? new Date(a.trial_ends_at).getTime() : now
  const cap = now + TRIAL_CAP_DAYS * DAY
  const trialIso = (ms: number) => new Date(Math.min(Math.max(ms, now), cap)).toISOString()

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[1.15fr_1fr]">
      <div className="grid content-start gap-4">
        <Card>
          <CardHeader title="Status" />
          <div className="flex flex-wrap items-center gap-2">
            <AccountStatusPill account={a} />
            {!suspended ? <LivePill isLive={a.is_live} /> : null}
          </div>
          <p className="mt-3 text-[13px] text-ink/90">{statusSentence(tenant, host)}</p>

          {warning === 'stripe_managed' ? (
            <InlineAlert tone="warn" className="mt-3" title="Stripe manages this subscription">
              Stripe may overwrite manual status changes at the next billing event. Change the subscription in Stripe to make it stick.
            </InlineAlert>
          ) : null}

          {suspended ? (
            <InlineAlert tone="bad" className="mt-4" title="Suspended">
              Status and trial changes are locked while the tenant is suspended.{' '}
              <Link to="../danger-zone" className="font-semibold underline underline-offset-2">
                Reinstate it in the Danger zone
              </Link>{' '}
              first.
            </InlineAlert>
          ) : (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant={a.status === 'trialing' ? 'secondary' : 'primary'} onClick={() => request({ label: `Start ${DEFAULT_TRIAL_DAYS}-day trial`, patch: { status: 'trialing', trial_ends_at: trialIso(now + DEFAULT_TRIAL_DAYS * DAY) } })}>
                  {a.status === 'trialing' ? `Restart ${DEFAULT_TRIAL_DAYS}-day trial` : `Start ${DEFAULT_TRIAL_DAYS}-day trial`}
                </Button>
                <Button size="sm" disabled={a.status === 'active'} onClick={() => request({ label: 'Mark active', patch: { status: 'active' } })}>
                  Mark active
                </Button>
                <Button size="sm" disabled={a.status === 'past_due'} onClick={() => request({ label: 'Mark past due', patch: { status: 'past_due' } })}>
                  Mark past due
                </Button>
                <Button size="sm" disabled={a.status === 'canceled'} onClick={() => request({ label: 'Mark canceled', patch: { status: 'canceled' } })}>
                  Mark canceled
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted">Active and past due keep the site live. A trial is live until it ends. Canceled takes it offline.</p>
              <SaveStatus state={action} className="mt-2" />
            </>
          )}
        </Card>

        <Card>
          <CardHeader title="Trial" description={`Capped at ${TRIAL_CAP_DAYS} days from today.`} />
          <KeyValueList
            items={[
              { label: 'Ends', value: formatDateTime(a.trial_ends_at) },
              {
                label: 'Left',
                value:
                  trialLeft === null ? (
                    <span className="text-muted">No trial on record</span>
                  ) : trialLeft > 0 ? (
                    `${trialLeft} day${trialLeft === 1 ? '' : 's'}`
                  ) : (
                    <span className="text-warn">Ended {formatDate(a.trial_ends_at)}</span>
                  ),
              },
            ]}
          />
          {a.status !== 'trialing' && !suspended ? <p className="mt-3 text-xs text-muted">The trial end only matters while the status is Trial. Use “Start trial” to put the tenant on one.</p> : null}
          {!suspended ? (
            <TrialControls
              disabled={a.status !== 'trialing'}
              canAdd={trialBase < cap - DAY / 2}
              onShift={(days) => {
                const next = trialIso(trialBase + days * DAY)
                request({ label: days > 0 ? `Extend trial ${days} days` : 'Shorten trial', patch: { trial_ends_at: next } })
              }}
              onSetDays={(days) => request({ label: `Set trial to ${days} days`, patch: { trial_ends_at: trialIso(now + days * DAY) } })}
            />
          ) : null}
        </Card>
      </div>

      <div className="grid content-start gap-4">
        <BillingCard tenant={tenant} patchAccount={patchAccount} />
        <p className="px-1 text-xs text-muted">
          Need to lock the site for another reason?{' '}
          <Link to="../danger-zone" className="font-semibold text-accent hover:underline">
            Suspend it in the Danger zone
          </Link>
          .
        </p>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={`Take ${tenant.name} offline?`}
        description={
          <>
            <span className="font-semibold text-ink">{confirm?.label}</span> takes the public site{host ? <> at <span className="break-all font-mono">{host}</span></> : null} offline right away. Fans see a “not available” page until the account is live again. Nothing is deleted.
          </>
        }
        confirmLabel={confirm?.label ?? 'Confirm'}
        onConfirm={async () => {
          if (confirm) await patchAccount(confirm.patch)
        }}
      />
    </div>
  )
}

function statusSentence(t: TenantDetail, host: string | null): ReactNode {
  const a = t.account
  const site = host ? `The public site (${host})` : 'The public site'
  switch (a.status) {
    case 'trialing': {
      const left = daysUntil(a.trial_ends_at)
      return a.is_live
        ? `${site} is live until the trial ends on ${formatDate(a.trial_ends_at)}${left !== null ? ` (${left} day${left === 1 ? '' : 's'} left)` : ''}.`
        : `The trial ended on ${formatDate(a.trial_ends_at)}, so ${site.toLowerCase()} is offline. Extend the trial or mark the account active.`
    }
    case 'active':
      return a.billing_source === 'stripe'
        ? `${site} is live. Billed through Stripe${a.current_period_end ? `; renews ${formatDate(a.current_period_end)}` : ''}.`
        : `${site} is live. Activated manually, with no Stripe billing.`
    case 'past_due':
      return `${site} is still live during the grace period, but the last payment failed${a.current_period_end ? ` (period ended ${formatDate(a.current_period_end)})` : ''}.`
    case 'suspended':
      return `${site}, Fan AI and the league admin are locked${a.suspension ? ` since ${formatDate(a.suspension.suspended_at)}` : ''}.`
    case 'canceled':
      return a.legacy_status === 'none'
        ? `No subscription on record, so ${site.toLowerCase()} is offline.`
        : `Not a customer any more, so ${site.toLowerCase()} is offline.`
  }
}

function TrialControls({
  disabled,
  canAdd,
  onShift,
  onSetDays,
}: {
  disabled: boolean
  canAdd: boolean
  onShift: (days: number) => void
  onSetDays: (days: number) => void
}) {
  const [days, setDays] = useState(String(DEFAULT_TRIAL_DAYS))
  const [error, setError] = useState<string | null>(null)
  const submit = () => {
    const n = Number(days)
    if (!Number.isInteger(n) || n < 1 || n > TRIAL_CAP_DAYS) {
      setError(`Enter a whole number of days from 1 to ${TRIAL_CAP_DAYS}.`)
      return
    }
    setError(null)
    onSetDays(n)
  }
  return (
    <div className="mt-4 grid gap-3 border-t border-line pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={disabled} onClick={() => onShift(-7)} aria-label="Shorten trial by 7 days">
          −7 days
        </Button>
        <Button size="sm" disabled={disabled || !canAdd} onClick={() => onShift(7)} aria-label="Extend trial by 7 days" title={canAdd ? undefined : `Capped at ${TRIAL_CAP_DAYS} days from today`}>
          +7 days
        </Button>
      </div>
      <form
        noValidate
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <FormField label="Days from today" error={error} className="w-36">
          {({ id, describedBy, invalid }) => (
            <TextInput id={id} aria-describedby={describedBy} invalid={invalid} type="number" min={1} max={TRIAL_CAP_DAYS} inputMode="numeric" value={days} disabled={disabled} onChange={(e) => setDays(e.target.value)} />
          )}
        </FormField>
        <Button type="submit" size="md" disabled={disabled} className={cn(error ? 'mb-6' : '')}>
          Set trial end
        </Button>
      </form>
    </div>
  )
}

function BillingCard({ tenant, patchAccount }: { tenant: TenantDetail; patchAccount: (p: AccountPatch) => Promise<void> }) {
  const a = tenant.account
  const pkg = a.package_key ?? 'none'
  const pkgOptions = [
    { value: 'standard', label: 'Standard' },
    { value: 'none', label: 'No package (add-ons only)' },
  ]
  if (pkg !== 'standard' && pkg !== 'none') pkgOptions.unshift({ value: pkg, label: `${a.package_name || pkg} (legacy)` })

  return (
    <Card>
      <CardHeader title="Billing" description="Each field saves on its own." />
      <div className="grid gap-5">
        <KeyValueList items={[{ label: 'Billing', value: BILLING_LABEL[a.billing_source] }]} />
        <EditableSelectField
          label="Package"
          hint="Add-ons can still be switched on or off individually."
          value={pkg}
          options={pkgOptions}
          onSave={(next) => patchAccount({ package_key: next === 'none' ? null : (next as 'standard') })}
        />
        <PeriodEndField value={a.current_period_end} onSave={(iso) => patchAccount({ current_period_end: iso })} />
        <EditableTextField
          label="Stripe customer id"
          mono
          value={a.stripe_customer_id ?? ''}
          placeholder="cus_…"
          hint="Empty to unlink. Set by the Stripe webhook after checkout."
          validate={(v) => (v && !v.startsWith('cus_') ? 'Stripe customer ids start with cus_.' : null)}
          onSave={(v) => patchAccount({ stripe_customer_id: v || null })}
        />
        <EditableTextField
          label="Stripe subscription id"
          mono
          value={a.stripe_subscription_id ?? ''}
          placeholder="sub_…"
          hint="Empty to unlink."
          validate={(v) => (v && !v.startsWith('sub_') ? 'Stripe subscription ids start with sub_.' : null)}
          onSave={(v) => patchAccount({ stripe_subscription_id: v || null })}
        />
        <EditableTextField
          label="Notes"
          multiline
          value={a.notes ?? ''}
          placeholder="Deal terms, contacts, anything the next person should know"
          hint="Only super-admins see these. Saves when you leave the field (or Ctrl/⌘+Enter)."
          onSave={(v) => patchAccount({ notes: v || null })}
        />
      </div>
    </Card>
  )
}

/** Date-only field for current_period_end; saves on change. */
function PeriodEndField({ value, onSave }: { value: string | null; onSave: (iso: string | null) => Promise<void> }) {
  const save = useSaveAction()
  const current = value ? toDateInput(new Date(value)) : ''
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? current
  const commit = async (next: string) => {
    if (next === current) return
    setDraft(next)
    // On success the new value arrives with the tenant; on failure the field
    // falls back to the stored date. Either way, drop the draft.
    await save.run(() => onSave(next ? new Date(`${next}T12:00:00`).toISOString() : null))
    setDraft(null)
  }
  return (
    <FormField label="Current period ends" hint="Normally set by Stripe. Empty to clear." aside={<SaveStatus state={save} onRetry={() => commit(shown)} />}>
      {({ id, describedBy }) => (
        <TextInput id={id} aria-describedby={describedBy} type="date" value={shown} disabled={save.status === 'saving'} onChange={(e) => commit(e.target.value)} className="max-w-[12rem]" />
      )}
    </FormField>
  )
}
